if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.zoomImage = null;
        this.pointers = new Map();
        this.dragStart = null;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;

        this.onWheel = this.onWheel.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.content = this.querySelector('.product-media-modal__content');
        this.content.addEventListener('wheel', this.onWheel, { passive: false });
        this.content.addEventListener('pointerdown', this.onPointerDown);
        this.content.addEventListener('pointermove', this.onPointerMove);
        this.content.addEventListener('pointerup', this.onPointerUp);
        this.content.addEventListener('pointercancel', this.onPointerUp);
        this.content.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.content.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.content.addEventListener('touchend', this.onTouchEnd);
        this.content.addEventListener('touchcancel', this.onTouchEnd);
        this.content.addEventListener('dragstart', (event) => {
          if (event.target.closest('img[data-media-id]')) event.preventDefault();
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.resetZoom();
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => element.classList.remove('active'));

        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      onWheel(event) {
        const image = event.target.closest('img[data-media-id]');
        if (!image || !this.contains(image)) return;

        event.preventDefault();
        this.selectZoomImage(image);
        const factor = Math.exp(-event.deltaY * 0.002);
        this.zoomAt(this.scale * factor, event.clientX, event.clientY);
      }

      onPointerDown(event) {
        if (event.pointerType === 'touch') return;
        const image = event.target.closest('img[data-media-id]');
        if (!image || !this.contains(image)) return;

        event.preventDefault();
        event.stopPropagation();
        this.selectZoomImage(image);
        this.content.setPointerCapture?.(event.pointerId);
        this.pointers.set(event.pointerId, event);

        if (this.pointers.size === 2) {
          const [first, second] = this.pointers.values();
          this.pinchStartDistance = this.distance(first, second);
          this.pinchStartScale = this.scale;
          this.dragStart = null;
        } else {
          this.dragStart = {
            clientX: event.clientX,
            clientY: event.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
        }
      }

      onPointerMove(event) {
        if (event.pointerType === 'touch') return;
        if (!this.pointers.has(event.pointerId) || !this.zoomImage) return;
        event.stopPropagation();
        this.pointers.set(event.pointerId, event);

        if (this.pointers.size === 2) {
          event.preventDefault();
          const [first, second] = this.pointers.values();
          const currentDistance = this.distance(first, second);
          const midpointX = (first.clientX + second.clientX) / 2;
          const midpointY = (first.clientY + second.clientY) / 2;
          this.zoomAt(this.pinchStartScale * (currentDistance / this.pinchStartDistance), midpointX, midpointY);
          return;
        }

        if (this.scale > 1 && this.dragStart) {
          event.preventDefault();
          this.translateX = this.dragStart.translateX + event.clientX - this.dragStart.clientX;
          this.translateY = this.dragStart.translateY + event.clientY - this.dragStart.clientY;
          this.applyZoom();
        }
      }

      onPointerUp(event) {
        if (event.pointerType === 'touch') return;
        if (!this.pointers.has(event.pointerId)) return;
        event.stopPropagation();
        this.pointers.delete(event.pointerId);

        if (this.pointers.size === 1) {
          const remaining = this.pointers.values().next().value;
          this.dragStart = {
            clientX: remaining.clientX,
            clientY: remaining.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
        } else {
          this.dragStart = null;
        }
      }

      onTouchStart(event) {
        const image = event.target.closest('img[data-media-id]');
        if (!image || !this.contains(image)) return;

        event.preventDefault();
        event.stopPropagation();
        this.selectZoomImage(image);

        if (event.touches.length >= 2) {
          const first = event.touches[0];
          const second = event.touches[1];
          this.pinchStartDistance = this.distance(first, second);
          this.pinchStartScale = this.scale;
          this.dragStart = null;
        } else if (event.touches.length === 1) {
          const touch = event.touches[0];
          this.dragStart = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
        }
      }

      onTouchMove(event) {
        if (!this.zoomImage) return;

        if (event.touches.length >= 2) {
          event.preventDefault();
          event.stopPropagation();
          const first = event.touches[0];
          const second = event.touches[1];
          const currentDistance = this.distance(first, second);
          const midpointX = (first.clientX + second.clientX) / 2;
          const midpointY = (first.clientY + second.clientY) / 2;
          this.zoomAt(this.pinchStartScale * (currentDistance / this.pinchStartDistance), midpointX, midpointY);
        } else if (event.touches.length === 1 && this.scale > 1 && this.dragStart) {
          event.preventDefault();
          event.stopPropagation();
          const touch = event.touches[0];
          this.translateX = this.dragStart.translateX + touch.clientX - this.dragStart.clientX;
          this.translateY = this.dragStart.translateY + touch.clientY - this.dragStart.clientY;
          this.applyZoom();
        }
      }

      onTouchEnd(event) {
        event.stopPropagation();
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          this.dragStart = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
        } else {
          this.dragStart = null;
        }
      }

      selectZoomImage(image) {
        if (this.zoomImage === image) return;
        this.resetZoom();
        this.zoomImage = image;
      }

      zoomAt(requestedScale, clientX, clientY) {
        if (!this.zoomImage) return;
        const nextScale = this.clamp(requestedScale, 1, 4);
        const rect = this.zoomImage.getBoundingClientRect();
        const pointX = clientX - (rect.left + rect.width / 2);
        const pointY = clientY - (rect.top + rect.height / 2);
        const ratio = nextScale / this.scale;

        this.translateX = pointX - (pointX - this.translateX) * ratio;
        this.translateY = pointY - (pointY - this.translateY) * ratio;
        this.scale = nextScale;
        this.applyZoom();
      }

      applyZoom() {
        if (!this.zoomImage || !this.content) return;
        const bounded = this.clampTranslation(
          this.translateX,
          this.translateY,
          this.scale,
          this.content.clientWidth,
          this.content.clientHeight
        );
        this.translateX = bounded.x;
        this.translateY = bounded.y;
        this.zoomImage.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
        this.classList.toggle('is-zoomed', this.scale > 1);
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }

      distance(first, second) {
        return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
      }

      clampTranslation(x, y, scale, width, height) {
        if (scale <= 1) return { x: 0, y: 0 };
        const maxX = (width * (scale - 1)) / 2;
        const maxY = (height * (scale - 1)) / 2;
        return {
          x: this.clamp(x, -maxX, maxX),
          y: this.clamp(y, -maxY, maxY),
        };
      }

      resetZoom() {
        this.querySelectorAll('img[data-media-id]').forEach((image) => image.style.removeProperty('transform'));
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.zoomImage = null;
        this.pointers.clear();
        this.dragStart = null;
        this.classList.remove('is-zoomed');
      }
    }
  );
}